// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import { Anchor, Badge, Button, Code, CopyButton, Divider, Group, Paper, Stack, Text } from '@mantine/core';
import { showNotification } from '@mantine/notifications';
import { normalizeErrorString } from '@medplum/core';
import { useMedplum } from '@medplum/react';
import { IconCheck, IconCopy, IconExternalLink, IconPlayerPlay, IconTrash } from '@tabler/icons-react';
import type { JSX } from 'react';
import { useCallback, useState } from 'react';
import { PageHeader } from '../../components/layout/PageHeader';
import {
  cleanupTaggedResources,
  type RunOnboardingChainResult,
  runOnboardingChain,
  type StepLogEntry,
} from './zoomIntegrationRunner';

type Status = 'idle' | 'running' | 'success' | 'error' | 'cleaning';

export function ZoomIntegrationTest(): JSX.Element {
  const medplum = useMedplum();
  const [status, setStatus] = useState<Status>('idle');
  const [log, setLog] = useState<StepLogEntry[]>([]);
  const [result, setResult] = useState<RunOnboardingChainResult | undefined>();

  const appendLog = useCallback((entry: StepLogEntry) => {
    setLog((prev) => [...prev, entry]);
  }, []);

  const handleRun = useCallback(async () => {
    setStatus('running');
    setLog([]);
    setResult(undefined);
    try {
      const r = await runOnboardingChain(medplum, appendLog);
      setResult(r);
      setStatus('success');
    } catch (err) {
      appendLog({ level: 'error', message: normalizeErrorString(err) });
      setStatus('error');
    }
  }, [medplum, appendLog]);

  const handleCleanup = useCallback(async () => {
    setStatus('cleaning');
    appendLog({ level: 'info', message: '--- Cleanup ---' });
    try {
      const counts = await cleanupTaggedResources(medplum, appendLog);
      const total = Object.values(counts).reduce((a, b) => a + b, 0);
      appendLog({ level: 'success', message: `Cleanup complete — ${total} resources deleted` });
      showNotification({ color: 'green', message: `Deleted ${total} tagged test resources.` });
      if (result) {
        setResult(undefined);
      }
      setStatus('idle');
    } catch (err) {
      appendLog({ level: 'error', message: normalizeErrorString(err) });
      setStatus('error');
    }
  }, [medplum, appendLog, result]);

  const isBusy = status === 'running' || status === 'cleaning';

  return (
    <Stack gap="md" p="md">
      <PageHeader title="Test Zoom integration" />

      <Paper withBorder radius="md" shadow="sm" p="md">
        <Stack gap="md">
          <Text c="dimmed" size="sm">
            Runs the full pre-auth onboarding chain end-to-end with the configured scheduling engine. Legacy mode seeds
            a test Slot; v2 finds and books a proposed Appointment through Medplum $find/$book. The chain then polls the
            resulting Appointment until the create-zoom-meetings subscription bot writes the telehealth extension. Every
            resource is tagged so you can clean them up below.
          </Text>

          <Group>
            <Button
              leftSection={<IconPlayerPlay size={16} />}
              onClick={() => {
                handleRun().catch(() => undefined);
              }}
              loading={status === 'running'}
              disabled={isBusy}
            >
              {status === 'running' ? 'Running…' : 'Run full chain'}
            </Button>
            <Button
              variant="default"
              leftSection={<IconTrash size={16} />}
              onClick={() => {
                handleCleanup().catch(() => undefined);
              }}
              loading={status === 'cleaning'}
              disabled={isBusy}
            >
              Clean up previous test runs
            </Button>
          </Group>
        </Stack>
      </Paper>

      {result && status === 'success' && <ResultCard result={result} />}

      {log.length > 0 && <LogCard entries={log} />}
    </Stack>
  );
}

function ResultCard({ result }: { result: RunOnboardingChainResult }): JSX.Element {
  return (
    <Paper withBorder radius="md" shadow="sm" p="md">
      <Stack gap="sm">
        <Group justify="space-between">
          <Text fw={600}>Zoom link ready</Text>
          <Badge color="green" variant="light">
            Success
          </Badge>
        </Group>
        <Divider />
        <Group>
          <Anchor href={result.zoomJoinUrl} target="_blank" rel="noreferrer">
            <Group gap={4}>
              <IconExternalLink size={14} />
              <Text>Open Zoom meeting</Text>
            </Group>
          </Anchor>
          <CopyButton value={result.zoomJoinUrl}>
            {({ copied, copy }) => (
              <Button
                size="xs"
                variant="light"
                leftSection={copied ? <IconCheck size={14} /> : <IconCopy size={14} />}
                onClick={copy}
              >
                {copied ? 'Copied' : 'Copy link'}
              </Button>
            )}
          </CopyButton>
        </Group>
        <Code block>{result.zoomJoinUrl}</Code>
        <Divider label="Seeded resources" labelPosition="left" />
        <ResourceLine label="Patient" id={result.patientId} />
        <ResourceLine label="Appointment" id={result.appointmentId} />
        <ResourceLine label="Encounter" id={result.encounterId} />
        <ResourceLine label="Slot" id={result.slotId} />
        <Text size="xs" c="dimmed">
          Onboarding uniqueId: {result.uniqueId}
        </Text>
      </Stack>
    </Paper>
  );
}

function ResourceLine({ label, id }: { label: string; id: string }): JSX.Element {
  return (
    <Group gap="xs">
      <Text size="sm" c="dimmed" w={110}>
        {label}
      </Text>
      <Code>{id}</Code>
    </Group>
  );
}

const LEVEL_COLORS: Record<StepLogEntry['level'], string> = {
  error: 'red',
  success: 'green',
  info: 'gray',
};

function LogCard({ entries }: { entries: StepLogEntry[] }): JSX.Element {
  return (
    <Paper withBorder radius="md" shadow="sm" p="md">
      <Stack gap={4}>
        <Text fw={600} mb="xs">
          Run log
        </Text>
        {entries.map((entry, idx) => (
          <Group key={idx} gap="xs" align="flex-start" wrap="nowrap">
            <Badge color={LEVEL_COLORS[entry.level]} size="xs">
              {entry.level}
            </Badge>
            <Text size="sm" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
              {entry.message}
            </Text>
          </Group>
        ))}
      </Stack>
    </Paper>
  );
}
